import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { 
  useMediaQuery, useTheme, Box, IconButton, Typography,
  Button, Dialog, DialogTitle, DialogActions 
} from '@mui/material';
import styles from './ScheduleTable.module.css';
import ScheduleFormModal from './ScheduleFormModal';
import { fetchWithAuth } from '../utils/api';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAuth } from '../contexts/AuthContext';
import { SseEvent } from '../utils/sse';
import MonthlyCalendarView from './MonthlyCalendarView';
import { Subscription } from 'rxjs';
import { saveCalendarViewMode, getCalendarViewMode } from '../utils/storageUtils';
import { getHolidayName } from '../utils/holidayUtils';
import { UserRole } from '../types/auth';

// 型定義
import { Schedule, EmployeeSchedule, Employee } from '../types/scheduleTypes';

// ヘルパー関数 - コンポーネントの外部に定義
import { 
  getStartOfWeek, 
  formatTime, 
  formatDate, 
  updateDateKeepTime,
  formatDateTimeForDisplay, 
  isSameDay 
} from '../utils/dateUtils';
import { API_BASE_URL } from '../config/constants';

interface ScheduleTableProps {
  userRole: UserRole;
}

// Using DropResult type from react-beautiful-dnd instead of custom interface

// ★★★ 新しいカスタムフック: 週の日付管理 ★★★
const useWeekDates = (initialDate = new Date()) => {
  const [startDate, setStartDate] = useState(getStartOfWeek(initialDate));
  
  const weekDates = useMemo(() => {
    const startOfWeek = getStartOfWeek(startDate);
    const dates = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      dates.push(formattedDate);
    }
    
    return dates;
  }, [startDate]);
  
  const weekRangeText = useMemo(() => {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    
    const startMonth = startDate.getMonth() + 1;
    const startDay = startDate.getDate();
    const endMonth = endDate.getMonth() + 1;
    const endDay = endDate.getDate();
    
    return `${startMonth}月${startDay}日 〜 ${endMonth}月${endDay}日`;
  }, [startDate]);
  
  const goToPreviousWeek = useCallback(() => {
    setStartDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() - 7);
      return newDate;
    });
  }, []);

  const goToNextWeek = useCallback(() => {
    setStartDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(prevDate.getDate() + 7);
      return newDate;
    });
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setStartDate(getStartOfWeek(new Date()));
  }, []);
  
  return {
    startDate,
    weekDates,
    weekRangeText,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek
  };
};

// ★★★ 新しいカスタムフック: スケジュールデータ管理 ★★★
const useScheduleData = (startDate: Date, weekDates: string[]) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [groupedSchedules, setGroupedSchedules] = useState<{ [employee: string]: EmployeeSchedule }>({});
  const [isEmployeesLoaded, setIsEmployeesLoaded] = useState(false);
  const { user } = useAuth();
  
  // データ取得関数
  const fetchEmployees = useCallback(async () => {
    console.log('従業員データ取得を開始');
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/employees`);
      
      if (!response.ok) {
        throw new Error('従業員情報の取得に失敗しました');
      }
      
      const data = await response.json();
      console.log(`従業員データ取得完了: ${data.length}件`);
      setEmployees(data);
      setIsEmployeesLoaded(true);
    } catch (error) {
      console.error('従業員情報の取得に失敗しました', error);
    }
  }, []);

  // 従業員マッピング
  const employeeIdToNameMap = useMemo(() => {
    const map = new Map<number, string>();
    employees.forEach(emp => map.set(emp.id, emp.name));
    return map;
  }, [employees]);

  const employeeNameToIdMap = useMemo(() => {
    const map = new Map<string, number>();
    employees.forEach(emp => map.set(emp.name, emp.id));
    return map;
  }, [employees]);
  
  // スケジュールデータ処理 - employees参照を関数内で使用するよう修正
  const processSchedules = useCallback((data: Schedule[], currentEmployees: Employee[]) => {
    console.log(`スケジュールデータ処理: ${data.length}件, 従業員: ${currentEmployees.length}件`);
    const grouped: { [employee: string]: EmployeeSchedule } = {};

    // 従業員ごとの空の構造を作成
    currentEmployees.forEach(employee => {
      grouped[employee.name] = {};
      
      // 各日付に対して空の配列を初期化
      weekDates.forEach(date => {
        grouped[employee.name][date] = [];
      });
    });

    // スケジュールデータを振り分け
    data.forEach((schedule) => {
      try {
        const dateObj = new Date(schedule.startTime);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // 週の範囲内かチェック
        if (!weekDates.includes(dateStr)) {
          return; // 範囲外はスキップ
        }
    
        // 従業員名が初期化されていない場合の対応
        if (!grouped[schedule.employee.name]) {
          grouped[schedule.employee.name] = {};
        }
        
        // 日付が初期化されていない場合の対応
        if (!grouped[schedule.employee.name][dateStr]) {
          grouped[schedule.employee.name][dateStr] = [];
        }
    
        grouped[schedule.employee.name][dateStr].push(schedule);
      } catch (error) {
        console.error('スケジュール処理エラー:', schedule, error);
      }
    });

    setGroupedSchedules(grouped);
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDates]);
  
  // fetchSchedules関数 - employeesへの依存を除去
  const fetchSchedules = useCallback(async () => {
    console.log(`スケジュールデータ取得開始: ${startDate.toISOString().split('T')[0]}`);
    
    // 従業員データがなければ取得しない
    if (employees.length === 0) {
      console.log('従業員データが空のため、スケジュール取得をスキップ');
      return;
    }
    
    try {
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = new Date(endDate.setDate(endDate.getDate() + 1))
        .toISOString().split('T')[0];
      
      const response = await fetchWithAuth(
        `${API_BASE_URL}/schedules?startDate=${startDateStr}&endDate=${endDateStr}`
      );
      
      if (!response.ok) {
        throw new Error('スケジュールの取得に失敗しました');
      }
      
      const data: Schedule[] = await response.json();
      console.log(`スケジュールデータ取得完了: ${data.length}件`);
      // 現在のemployeesを直接関数に渡す
      processSchedules(data, employees);
    } catch (error) {
      console.error('スケジュールの取得に失敗しました', error);
    }
  }, [startDate, processSchedules, employees]);
  
  // 従業員のソート処理
  const sortedEmployeeNames = useMemo(() => {
    const employeeNames = Object.keys(groupedSchedules);
    
    // ログインユーザーがない場合はそのまま返す
    if (!user?.employeeId) {
      return employeeNames;
    }
    
    // ログインユーザーの従業員名を取得
    const currentUserEmployeeName = employeeIdToNameMap.get(user.employeeId);
    if (!currentUserEmployeeName || !employeeNames.includes(currentUserEmployeeName)) {
      return employeeNames;
    }
    
    // ユーザーの従業員を先頭に、それ以外は元の順序で
    return [
      currentUserEmployeeName,
      ...employeeNames.filter(name => name !== currentUserEmployeeName)
    ];
  }, [groupedSchedules, employeeIdToNameMap, user]);
  
  // 初期データ読み込み
  useEffect(() => {
    console.log('従業員データ初期化');
    // コンポーネントマウント時に1回だけ取得
    fetchEmployees();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // スケジュールは従業員データ取得後かつ日付変更時のみ取得
  useEffect(() => {
    if (!isEmployeesLoaded) {
      // 従業員データが読み込まれていない場合はスキップ
      console.log('従業員データ未ロードのためスケジュール取得をスキップ');
      return;
    }

    console.log(`スケジュール取得を実行: ${startDate.toISOString()}`);
    const controller = new AbortController();
    
    fetchSchedules();
    
    return () => {
      console.log('進行中のスケジュール取得をキャンセル');
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, isEmployeesLoaded]); // fetchSchedulesではなく日付とフラグに依存
  
  return {
    employees,
    groupedSchedules,
    employeeIdToNameMap,
    employeeNameToIdMap,
    sortedEmployeeNames,
    fetchEmployees,
    fetchSchedules,
    isEmployeesLoaded
  };
};

// ★★★ 修正したカスタムフック: SSE通知の管理 ★★★
const useSseNotifications = (fetchSchedules: () => Promise<void>) => {
  const [notification, setNotification] = useState<{message: string, type: 'info' | 'success' | 'error'} | null>(null);
  const { sseEvents } = useAuth(); // AuthContextから取得

  // fetchSchedules の参照を useRef に保存して安定化
  const fetchSchedulesRef = useRef(fetchSchedules);
  
  // fetchSchedules が変わったときだけ ref を更新
  useEffect(() => {
    fetchSchedulesRef.current = fetchSchedules;
  }, [fetchSchedules]);
  
  // SSEイベント処理 - 独自に接続せず、AuthContextから提供されるイベントを購読
  useEffect(() => {
    let subscription: Subscription | null = null;
    
    if (sseEvents) {
      subscription = sseEvents.subscribe({
        next: (event: SseEvent) => {
          console.log('ScheduleTable: イベント受信:', event.type);
          
          switch (event.type) {
            case 'schedule-created':
            case 'schedule_created':
              setNotification({ message: 'スケジュールが新しく追加されました', type: 'info' });
              // 安定化した参照を使用
              fetchSchedulesRef.current();
              break;
              
            case 'schedule-updated':
            case 'schedule_updated':
              setNotification({ message: 'スケジュールが更新されました', type: 'info' });
              fetchSchedulesRef.current();
              break;
              
            case 'schedule-deleted':
            case 'schedule_deleted':
              setNotification({ message: 'スケジュールが削除されました', type: 'info' });
              fetchSchedulesRef.current();
              break;
              
            case 'error':
              // エラー処理...
              break;
          }
        },
        error: (error) => {
          console.error('ScheduleTable: SSEエラー:', error);
        }
      });
      
      console.log('ScheduleTable: SSEイベント購読を開始しました');
    }
    
    return () => {
      if (subscription) {
        subscription.unsubscribe();
        console.log('ScheduleTable: SSEイベント購読を解除しました');
      }
    };
  }, [sseEvents]);

  // 通知の自動クローズ
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  return {
    notification,
    setNotification
  };
};

// ★★★ サブコンポーネント: スケジュールカード ★★★
const ScheduleCard = React.memo(({
  schedule,
  index,
  canEdit,
  canViewDetails,
  onScheduleClick
}: {
  schedule: Schedule;
  index: number;
  canEdit: boolean;
  canViewDetails: boolean;
  onScheduleClick: (schedule: Schedule) => void;
}) => {
  // 休暇かどうかを判断する
  const isVacation = schedule.task.includes('休暇') || schedule.task.toLowerCase().includes('vacation');
  
  // 完了状態のチェック
  const isCompleted = schedule.isCompleted === true;
  
  // 権限、休暇状態、完了状態に基づいてスタイルを適用
  let cardClassName: string; // 型を明示的に指定
  
  if (isCompleted) {
    // 完了状態の場合
    cardClassName = !canEdit ? styles.scheduleItemReadOnlyCompleted : styles.scheduleItemCompleted;
  } else if (isVacation) {
    // 休暇の場合
    cardClassName = !canEdit ? styles.scheduleItemReadOnlyVacation : styles.scheduleItemVacation;
  } else {
    // 通常の場合
    cardClassName = !canEdit ? styles.scheduleItemReadOnly : styles.scheduleItem;
  }
  
  return (
    <Draggable
      key={schedule.id}
      draggableId={schedule.id.toString()}
      index={index}
      isDragDisabled={!canEdit}
    >
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cardClassName}
          onClick={(e) => {
            e.stopPropagation();
            onScheduleClick(schedule);
          }}
        >
          <div className={styles.scheduleTime}>
            {`${formatTime(schedule.startTime)} ~ ${formatTime(schedule.endTime)}`}
          </div>
          {canViewDetails ? (
            <>
              {schedule.title && (
                <div className={styles.scheduleTitle}>
                  {schedule.title}
                </div>
              )}
              <div className={styles.scheduleTask}>
                {schedule.task}
              </div>
            </>
          ) : (
            <div className={styles.scheduleTask}>
              予約済み
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
});

// ★★★ サブコンポーネント: 通知表示 ★★★
interface NotificationBannerProps {
  notification: { message: string; type: 'info' | 'success' | 'error' } | null;
  onClose: () => void;
}

const NotificationBanner = React.memo<NotificationBannerProps>(({ notification, onClose }) => {
  if (!notification) return null;
  
  return (
    <div className={`${styles.notification} ${styles[notification.type]}`}>
      {notification.message}
      <button onClick={onClose}>×</button>
    </div>
  );
});

// メイン・コンポーネント
const ScheduleTable: React.FC<ScheduleTableProps> = ({ userRole }) => {
  const { user, canEditSchedules, canViewScheduleDetails } = useAuth();
  const userEmployeeId = user?.employeeId || null;
  
  // 権限に基づく動作制御
  const canEdit = canEditSchedules();
  const canViewDetails = canViewScheduleDetails();
  
  // カスタムフックの使用
  const {
    startDate,
    weekDates,
    weekRangeText,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek
  } = useWeekDates();
  
  const {
    groupedSchedules,
    employeeNameToIdMap,
    sortedEmployeeNames,
    fetchSchedules
  } = useScheduleData(startDate, weekDates);
  
  const { notification, setNotification } = useSseNotifications(fetchSchedules);
  
  // ビューモード状態 - ユーティリティ関数を使用してローカルストレージから初期値を取得
  const [viewMode, setViewMode] = useState<'week' | 'month'>(() => 
    getCalendarViewMode(user?.id)
  );

  // ビューモードが変更されたらローカルストレージに保存
  useEffect(() => {
    // ユーティリティ関数を使用して保存
    saveCalendarViewMode(viewMode, user?.id);
    console.log(`ビューモードを保存しました: ${viewMode}`);
  }, [viewMode, user?.id]);

  // 状態管理
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ employeeId: number; employeeName: string; date: string } | null>(null);
  const [dragResult, setDragResult] = useState<DropResult | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // イベントハンドラ
  const handleScheduleClick = useCallback((schedule: Schedule) => {
    // 詳細表示権限がなければスケジュールクリックは無効
    if (!canViewDetails) {
      console.log('権限不足のため予定詳細表示をスキップします');
      return;
    }
    
    console.log('予定カードがクリックされました - スケジュールID:', schedule.id);
    console.log('予定詳細:', { 
      id: schedule.id,
      title: schedule.title,
      task: schedule.task,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      employeeId: schedule.employee?.id
    });
    
    setSelectedSchedule(schedule);
  }, [canViewDetails]);

  const handleCellClick = useCallback((employeeId: number, employeeName: string, date: string) => {
    // 編集権限がなければセルクリックは無効
    if (!canEdit) return;

    const targetEmployeeId = employeeId || 
      employeeNameToIdMap.get(employeeName) || 0;
      
    setSelectedCell({ 
      employeeId: targetEmployeeId, 
      employeeName, 
      date 
    });
  }, [employeeNameToIdMap, canEdit]);

  // ドラッグ&ドロップ関連
  const createDroppableId = useCallback((employee: string, date: string) => {
    return `${employee}:${date}`;
  }, []);

  const parseDroppableId = useCallback((droppableId: string) => {
    const [employee, date] = droppableId.split(':');
    return { employee, date };
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    // 編集権限がなければドラッグは無効
    if (!result.destination || !canEdit) return;
  
    const { source, destination } = result;
    
    // 同じセルへのドロップは無視
    if (source.droppableId === destination.droppableId) {
      return;
    }
    // 確認ダイアログを表示
    setDragResult(result);
    setConfirmDialogOpen(true);
  }, [canEdit]);
  
  const handleConfirmMove = useCallback(async () => {
    if (!dragResult || !dragResult.draggableId || !dragResult.destination) {
      setConfirmDialogOpen(false);
      return;
    }

    const scheduleId = parseInt(dragResult.draggableId);
    const destInfo = parseDroppableId(dragResult.destination.droppableId);
    
    // 全スケジュールから該当のスケジュールを探す
    const allSchedules = Object.values(groupedSchedules)
      .flatMap(employeeSchedules => 
        Object.values(employeeSchedules).flat()
      );
    const schedule = allSchedules.find(s => s.id === scheduleId);
    
    if (!schedule) {
      console.error('スケジュールが見つかりません');
      setConfirmDialogOpen(false);
      setDragResult(null);
      return;
    }
    
    // 移動先の従業員IDを取得
    const targetEmployeeId = employeeNameToIdMap.get(destInfo.employee);
    if (!targetEmployeeId) {
      console.error('移動先の従業員が見つかりません');
      setConfirmDialogOpen(false);
      setDragResult(null);
      return;
    }
  
    try {
      // 開始時刻と終了時刻を新しい日付に更新
      const newStartTime = updateDateKeepTime(schedule.startTime, destInfo.date);
      const newEndTime = updateDateKeepTime(schedule.endTime, destInfo.date);
      
      // デバッグログを追加
      console.log(`スケジュール移動: ${formatDateTimeForDisplay(schedule.startTime)} → ${formatDateTimeForDisplay(newStartTime)}`);
      
      // 送信するデータを構築
      const updatedData = {
        employeeId: targetEmployeeId,
        projectId: schedule.project?.id,
        title: schedule.title || '',
        task: schedule.task,
        startTime: newStartTime,
        endTime: newEndTime
      };
  
      const response = await fetchWithAuth(
        `${API_BASE_URL}/schedules/${schedule.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedData),
        }
      );
  
      if (!response.ok) {
        throw new Error(`予定の更新に失敗しました`);
      }
      
      fetchSchedules();
    } catch (error) {
      console.error('予定の移動に失敗しました', error);
    } finally {
      setConfirmDialogOpen(false);
      setDragResult(null);
    }
  }, [dragResult, parseDroppableId, groupedSchedules, employeeNameToIdMap, fetchSchedules]);
  
  const handleCancelMove = useCallback(() => {
    setConfirmDialogOpen(false);
    setDragResult(null);
  }, []);

  // ビューモード切替処理
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'week' ? 'month' : 'week');
  }, []);

  // レスポンシブデザインのためのメディアクエリ
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 修正: formatDateを拡張して祝日情報を表示する
  const formatDateWithHoliday = (dateStr: string) => {
    const formattedDate = formatDate(dateStr);
    const holidayName = getHolidayName(dateStr);
    
    if (holidayName) {
      return (
        <div>
          <div>{formattedDate}</div>
          <div className={styles.holidayName}>{holidayName}</div>
        </div>
      );
    }
    
    return formattedDate;
  };

  return (
    <div className={styles.tableContainer}>
      {/* 通知コンポーネント */}
      <NotificationBanner 
        notification={notification} 
        onClose={() => setNotification(null)} 
      />
      
      {/* 表示モードによってコンポーネントを切り替え */}
      {viewMode === 'week' ? (
        <>
          {/* 週の切り替えコントロール - 移動ボタンと日付表示を左側に配置するよう修正 */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center', 
            mb: isMobile ? 1 : 2,
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}>
            {/* 左側に配置する移動ボタンと週表示 */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              flex: 2,
              order: isMobile ? 1 : 0,
              my: isMobile ? 1 : 0,
              width: isMobile ? '100%' : 'auto'
            }}>
              <IconButton 
                onClick={goToPreviousWeek} 
                color="primary" 
                size={isMobile ? "small" : "medium"}
                sx={{ mx: 0 }}
              >
                <ArrowBackIosNewIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
              
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                sx={{ mx: 1, minWidth: isMobile ? 'auto' : '180px', textAlign: 'center' }}
              >
                {weekRangeText}
              </Typography>
              
              <IconButton 
                onClick={goToNextWeek} 
                color="primary" 
                size={isMobile ? "small" : "medium"}
                sx={{ mx: 0 }}
              >
                <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Box>
            
            {/* 中央のスペース（空白） */}
            <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
            
            {/* 右側に配置するコントロールグループ */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              flex: 2,
              order: isMobile ? 0 : 2
            }}>
              <Button 
                variant="outlined" 
                startIcon={<TodayIcon />} 
                onClick={goToCurrentWeek}
                size="small"
                sx={{ mr: 1, fontSize: isMobile ? '0.7rem' : '0.8rem' }}
              >
                今週
              </Button>
              
              {/* カレンダー表示切替ボタン - 従業員IDがある場合のみ表示 */}
              {userEmployeeId && (
                <Button
                  variant="contained"
                  startIcon={<CalendarMonthIcon />}
                  onClick={toggleViewMode}
                  size="small"
                  sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                >
                  {isMobile ? '月表示' : '月カレンダー'}
                </Button>
              )}
            </Box>
          </Box>

          {/* スケジュールテーブル - スマホ対応のスタイル */}
          <div className={`${isMobile ? styles.mobileTableWrapper : styles.tableWrapper}`}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <table className={`${styles.table} ${isMobile ? styles.mobileTable : ''}`}>
                <thead className={styles.stickyHeader}>
                  <tr>
                    <th className={`${styles.th} ${styles.employeeColumn} ${styles.stickyColumn}`}>
                        作業員
                    </th>
                    {weekDates.map((date) => (
                        <th key={date} className={`${styles.th} ${getHolidayName(date) ? styles.holidayHeader : ''}`}>
                        {formatDateWithHoliday(date)}
                        </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedEmployeeNames.map((employee) => (
                    <tr key={employee}>
                      <td className={`${styles.td} ${styles.employeeColumn} ${styles.stickyColumn}`}>{employee}</td>
                      {weekDates.map((date) => {
                        const employeeId = employeeNameToIdMap.get(employee) || 0;
                          
                        return (
                          <Droppable 
                            droppableId={createDroppableId(employee, date)} 
                            key={date}
                            isDropDisabled={!canEdit}
                          >
                            {(provided) => (
                              <td
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={groupedSchedules[employee][date]?.length ? styles.td : styles.tdEmpty}
                                onClick={() => handleCellClick(employeeId, employee, date)}
                              >
                                <div className={styles.scheduleContainer}>
                                  {groupedSchedules[employee][date]?.map((schedule, index) => (
                                    <ScheduleCard
                                      key={schedule.id}
                                      schedule={schedule}
                                      index={index}
                                      canEdit={canEdit}
                                      canViewDetails={canViewDetails}
                                      onScheduleClick={handleScheduleClick}
                                    />
                                  ))}
                                  {provided.placeholder}
                                </div>
                              </td>
                            )}
                          </Droppable>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </DragDropContext>
          </div>
        </>
      ) : (
        /* 月カレンダー表示 */
        <MonthlyCalendarView
          userRole={userRole}
          employeeId={userEmployeeId}
          onSwitchToWeekView={toggleViewMode}
        />
      )}

      {/* モーダル - 権限に基づいて表示制御 */}
      {selectedCell && canEdit && (
        <ScheduleFormModal
          userRole={userRole}
          employeeId={selectedCell.employeeId}
          employeeName={selectedCell.employeeName}
          date={selectedCell.date}
          onClose={() => {
            console.log('新規予定モーダルを閉じます');
            setSelectedCell(null);
          }}
          onScheduleAdded={fetchSchedules}
        />
      )}

      {selectedSchedule && (
        <>
          {console.log('詳細モーダルを表示します - スケジュールID:', selectedSchedule.id)}
          <ScheduleFormModal
            userRole={userRole}
            initialData={selectedSchedule}
            onClose={() => {
              console.log('詳細モーダルを閉じます');
              setSelectedSchedule(null);
            }}
            onScheduleUpdated={fetchSchedules}
            onScheduleDeleted={fetchSchedules}
          />
        </>
      )}

      {/* 確認ダイアログは編集権限がある場合のみ表示 */}
      <Dialog
        open={confirmDialogOpen && canEdit}
        onClose={handleCancelMove}
      >
        <DialogTitle>予定を移動しますか？</DialogTitle>
        <DialogActions>
          <Button onClick={handleCancelMove} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleConfirmMove} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScheduleTable;
